import { useState } from 'react'
import { SkeletonList } from '../componentes/Skeleton'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import {
  useEjecucionesRecalculo,
  useCambiosEjecucion,
  useRecalcularSimulacion,
  type ResumenRecalculo,
  type EjecucionRecalculo,
} from '../hooks/useRecalculo'
import { BarraPaginacion } from '../componentes/BarraPaginacion'
import { usePaginacionLocal } from '../componentes/paginacion'

/**
 * Pantalla de recálculo de etiquetas S (A.2.1 · §9).
 *
 * El recálculo manual SIEMPRE se ejecuta en SIMULACIÓN: propone las S nuevas y
 * las registra para auditoría, pero no toca Holded. Así el equipo compara lo que
 * el sistema haría antes de habilitar la escritura real. Si el % de clientes
 * afectados supera el umbral, el propio backend se detiene (salvaguarda de
 * volumen anómalo) y aquí se avisa.
 */
function fmtFecha(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function ResumenSimulacion({ r }: { r: ResumenRecalculo }) {
  return (
    <div
      className={[
        'rounded-xl border p-4',
        r.detenida
          ? 'border-critico-anillo bg-critico-fondo'
          : 'border-positivo-anillo bg-positivo-fondo',
      ].join(' ')}
    >
      <p className="text-sm font-semibold text-tinta">
        {r.detenida ? 'Simulación detenida por salvaguarda' : 'Simulación completada'}
      </p>
      {r.detenida && r.motivoParada ? (
        <p className="mt-1 text-sm text-critico-tinta">{r.motivoParada}</p>
      ) : null}
      <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metrica etiqueta="Clientes" valor={r.totalClientes} />
        <Metrica etiqueta="Cambios" valor={r.totalCambios} />
        <Metrica etiqueta="% afectado" valor={`${r.pctAfectado}%`} />
        <Metrica etiqueta="Excluidos" valor={r.excluidos} />
      </dl>
    </div>
  )
}

function Metrica({ etiqueta, valor }: { etiqueta: string; valor: string | number }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-tinta-apagada">{etiqueta}</dt>
      <dd className="mt-0.5 font-display text-lg font-bold tabular-nums text-tinta">{valor}</dd>
    </div>
  )
}

function DetalleCambios({ ejecucionId }: { ejecucionId: string }) {
  const cambios = useCambiosEjecucion(ejecucionId)
  const pag = usePaginacionLocal(cambios.data ?? [], ejecucionId)
  if (cambios.isPending) return <SkeletonList />
  if (cambios.isError)
    return <EstadoError error={cambios.error} onReintentar={() => void cambios.refetch()} />
  if (pag.visibles.length === 0)
    return <p className="px-4 py-3 text-sm text-tinta-apagada">Esta ejecución no propuso cambios.</p>
  return (
    <>
      <ul className="divide-y divide-borde-suave">
        {pag.visibles.map((c) => (
          <li key={c.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
            <span className="truncate text-tinta-2">{c.contactId}</span>
            <span className="shrink-0 tabular-nums">
              <span className="text-tinta-apagada">{c.valorAnterior ?? '—'}</span>
              <span className="mx-1 text-tinta-apagada">→</span>
              <span className="font-semibold text-tinta">{c.valorNuevo ?? '—'}</span>
            </span>
          </li>
        ))}
      </ul>
      <div className="px-4 pb-3">
        <BarraPaginacion pagina={pag.pagina} paginas={pag.paginas} onCambiar={pag.irAPagina} etiqueta="Paginación de los cambios" />
      </div>
    </>
  )
}

function FilaEjecucion({ e }: { e: EjecucionRecalculo }) {
  const [abierto, setAbierto] = useState(false)
  return (
    <li className="overflow-hidden rounded-xl border border-borde bg-superficie">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-elevado"
      >
        <span className="flex min-w-0 flex-col">
          <span className="text-sm font-medium text-tinta">{fmtFecha(e.iniciadaEn)}</span>
          <span className="text-xs text-tinta-apagada">
            {e.modo === 'simulacion' ? 'Simulación' : 'Real'} · {e.totalCambios} cambios ·{' '}
            {e.pctAfectado}% afectado
            {e.detenida ? ' · detenida' : ''}
          </span>
        </span>
        <span className="shrink-0 text-xs text-acento-tinta">{abierto ? 'Ocultar' : 'Ver cambios'}</span>
      </button>
      {abierto ? (
        <div className="border-t border-borde-suave">
          <DetalleCambios ejecucionId={e.id} />
        </div>
      ) : null}
    </li>
  )
}

export function Recalculo() {
  const ejecuciones = useEjecucionesRecalculo()
  const simular = useRecalcularSimulacion()
  const items = ejecuciones.data ?? []
  const pag = usePaginacionLocal(items)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-tinta">Recálculo de etiquetas</h1>
          <p className="mt-1 max-w-2xl text-sm text-tinta-apagada">
            Calcula las etiquetas S según el último albarán de cada cliente. Se ejecuta en{' '}
            <strong>simulación</strong>: propone los cambios y los registra para auditoría, pero no
            cambia las etiquetas de los clientes. Así puedes revisarlos antes de activar la escritura real.
          </p>
        </div>
        <button
          type="button"
          disabled={simular.isPending}
          onClick={() => simular.mutate()}
          className="shrink-0 rounded-lg border border-acento-anillo bg-acento-fondo px-4 py-2 text-sm font-semibold text-acento-tinta transition hover:brightness-110 disabled:opacity-50"
        >
          {simular.isPending ? 'Simulando…' : 'Simular recálculo'}
        </button>
      </div>

      {simular.isError ? (
        <p role="alert" className="text-sm text-critico-tinta">
          No se ha podido lanzar la simulación. Inténtalo de nuevo.
        </p>
      ) : null}
      {simular.data ? <ResumenSimulacion r={simular.data} /> : null}

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-tinta">Ejecuciones anteriores</h2>
        {ejecuciones.isPending ? (
          <SkeletonList />
        ) : ejecuciones.isError ? (
          <EstadoError error={ejecuciones.error} onReintentar={() => void ejecuciones.refetch()} />
        ) : items.length === 0 ? (
          <EstadoVacio
            titulo="Sin ejecuciones todavía"
            descripcion="Lanza una simulación para ver aquí el histórico y los cambios propuestos."
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {pag.visibles.map((e) => (
              <FilaEjecucion key={e.id} e={e} />
            ))}
          </ul>
        )}
        <BarraPaginacion pagina={pag.pagina} paginas={pag.paginas} onCambiar={pag.irAPagina} />
      </section>
    </div>
  )
}
